<template>
    <MainLayout 
        :is-logged-in="isLoggedIn" 
        :header-title="header" 
        :step-number="stepNumber" 
        :progress="progress"
        :is-module-started="isModuleStarted"
    >
        <component :is="currentView" @updateHeader="updateHeader" />
    </MainLayout>
</template>

<script setup>
import { ref, computed, provide, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '../services/api';
import 'boxicons/css/boxicons.min.css';

const { t } = useI18n();

// Components
import MainLayout from './templates/MainLayout.vue';
import Home from '../views/Home.vue';
import Modules from '../views/Modules.vue';
import Module from '../views/Module.vue';
import KnowledgeBase from '../views/KnowledgeBase.vue';
import KnowledgeBaseItem from '../views/KnowledgeBaseItem.vue';
import JargonCards from '../views/JargonCards.vue';
import Videos from '../views/Videos.vue';
import Video from '../views/Video.vue';
import Articles from '../views/Articles.vue';
import News from '../views/News.vue';
import NewsItem from '../views/NewsItem.vue';
import ProofCards from '../views/ProofCards.vue';
import ProofCard from '../views/ProofCard.vue';
import Badges from '../views/Badges.vue';
import NotFound from '../views/NotFound.vue';
import Profile from '../views/Profile.vue';
import ChangePassword from '../views/ChangePassword.vue';
import Login from '../views/Login.vue';
import ImportantContacts from '../views/ImportantContacts.vue';
import ImportantContact from '../views/ImportantContact.vue';
import ArticleOverview from '../views/ArticleOverview.vue';

const routes = {
    '/': { component: Home, title: t('home.home'), backUrl: null },
    '/#/': { component: Home, title: t('home.home'), backUrl: null },
    '/#/home': { component: Home, title: t('home.home'), backUrl: null },
    '/#/modules': { component: Modules, title: t('module.modules'), backUrl: '/#/home' },
    '/#/modules/:id': { component: Module, title: t('module.module'), backUrl: '/#/modules' },
    '/#/knowledgebase': { component: KnowledgeBase, title: t('knowledgebase.knowledgebase'), backUrl: '/#/home' },
    '/#/knowledgebase/:id': { component: KnowledgeBaseItem, title: t('knowledgebase.knowledgebase'), backUrl: 'goBack' },
    '/#/jargoncards': { component: JargonCards, title: t('knowledgebase.jargonCards'), backUrl: '/#/knowledgebase' },
    '/#/videos': { component: Videos, title: t('knowledgebase.videos'), backUrl: '/#/knowledgebase' },
    '/#/videos/:id': { component: Video, title: t('knowledgebase.videos'), backUrl: '/#/videos' },
    '/#/importantcontacts': { component: ImportantContacts, title: t('knowledgebase.importantContacts'), backUrl: '/#/knowledgebase' },
    '/#/importantcontacts/:id': { component: ImportantContact, title: t('profile.manager'), backUrl: '/#/profile' },
    '/#/articles': { component: Articles, title: t('knowledgebase.articles'), backUrl: 'goBack' }, // Use goBack to use the default browser back functionality
    '/#/news': { component: News, title: t('news.news'), backUrl: '/#/home' },
    '/#/news/:id': { component: NewsItem, title: t('news.news'), backUrl: '/#/news' },
    '/#/proofcards': { component: ProofCards, title: t('proofCard.proofCards'), backUrl: '/#/profile' },
    '/#/proofcards/:id': { component: ProofCard, title: t('proofCard.proofCards'), backUrl: '/#/proofcards' },
    '/#/badges': { component: Badges, title: t('profile.badges'), backUrl: '/#/profile' },
    '/#/profile': { component: Profile, title: t('profile.profile'), backUrl: '/#/home' },
    '/#/changepassword': { component: ChangePassword, title: t('profile.changePassword'), backUrl: '/#/profile' },
    '/#/login': { component: Login, title: t('general.login'), backUrl: null }
};

const currentPath = ref(window.location.hash || '/#/');
if (currentPath.value === '/') {
    currentPath.value = '/#/home';
}

const currentId = ref(null);
const isLoggedIn = ref(false);
const header = ref('');
const progress = ref([]);
const stepNumber = ref(0);
const isModuleStarted = ref(false);

const updateHeader = (newHeader, number = 0, progressbar = [], percentage = 0, moduleStarted = false) => {
    header.value = newHeader;
    stepNumber.value = number;
    progress.value = progressbar;
    isModuleStarted.value = moduleStarted;
};

const updateHeaderBasedOnRoute = (id, path) => {
    if (id) {
        const basePath = '/'+path.replace(/\/\d+$/, '/:id');
        const route = routes[basePath];

        if (route) {
            updateHeader(route.title, 0);
        } else {
            updateHeader('', 0);
        }
    } else {
        const routePath = '/' + path || '/';
        const route = routes[routePath];
        const title = route ? route.title : '';
        updateHeader(title, 0);
    }
};

watch([currentId, currentPath], ([newId, newPath]) => {
    updateHeaderBasedOnRoute(newId, newPath);
});

const checkLogin = async () => {
    try {
        const response = await api.get('/check-login');

        if (response.status === 200) {
            isLoggedIn.value = response.data.logged_in || false;
        } else {
            isLoggedIn.value = false;
            window.location.href = '/login';
        }
    } catch (error) {
        console.error('Error checking login status:', error);
        isLoggedIn.value = false;
    }
};

window.addEventListener('hashchange', async () => {
    await checkLogin();
    if (isLoggedIn.value) {
        currentPath.value = window.location.hash || '/#/';
    } else {
        currentPath.value = '#/login';
    }
});

const currentView = computed(() => {
    const pathWithoutHash = currentPath.value.slice(2);
    const route = routes[currentPath.value] || routes[`/#/${pathWithoutHash}`];
    if (route) {
        currentId.value = null;
        return route.component || NotFound;
    }

    const pathMap = {
        '#/modules/': Module,
        '#/proofcards/': ProofCard,
        '#/knowledgebase/': KnowledgeBaseItem,
        '#/videos/': Video,
        '#/articles/': ArticleOverview,
        '#/news/': NewsItem,
        '#/importantcontacts/': ImportantContact,
    };

    for (const [prefix, component] of Object.entries(pathMap)) {
        if (currentPath.value.startsWith(prefix) && !/\d/.test(currentPath.value.replace(prefix, ''))) {
            currentId.value = null;
            return component;
        } else if (currentPath.value.startsWith(prefix) && /\d/.test(currentPath.value)) {
            currentId.value = currentPath.value.split('/').pop();
            return component;
        }
    }

    return NotFound;
});

provide('routes', routes);
provide('currentPath', currentPath);
provide('currentId', currentId);

onMounted(async () => {
    await checkLogin();
    updateHeaderBasedOnRoute(currentId.value, currentPath.value);
});
</script>
